import React, { useState, useMemo, useRef, useEffect } from 'react';
import ShareButton from './ui/ShareButton';
import ChartExportButton from './ui/ChartExportButton';
import CondensedName from './common/CondensedName';
import NLInterpretation from './NLInterpretation';
import axios from 'axios';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableSortLabel,
  TextField,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Popover,
  Collapse,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from '@mui/material';
import ScrollTable from './ui/ScrollTable';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GetAppIcon from '@mui/icons-material/GetApp';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import MapIcon from '@mui/icons-material/Map';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import ChartPanel from './ChartPanel';
import GraphicMaker from './GraphicMaker';
import { PitchMapContainer, getPitchMapMode } from './PitchMap';
import config from '../config';
import { qbButtonSx, qbCardSx, qbColors, qbFonts, qbGhostButtonSx } from './queryBuilderTheme';
import { DEFAULT_MIN_COVERAGE, TAGGED_METRICS, rankWithCoverage } from '../utils/coverage';
import { apiErrorText } from '../utils/apiError';

const darkOutlineChipSx = {
  bgcolor: 'rgba(255,255,255,0.035)',
  color: qbColors.textMed,
  borderColor: qbColors.borderStrong,
  maxWidth: '100%',
  '& .MuiChip-icon': { color: qbColors.textLo },
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& .MuiChip-deleteIcon': {
    color: qbColors.textFaint,
    '&:hover': { color: qbColors.red },
  },
};

const darkInfoAlertSx = {
  bgcolor: 'rgba(91,141,239,0.14)',
  color: qbColors.textMed,
  border: '1px solid rgba(91,141,239,0.34)',
  borderRadius: '10px',
  '& .MuiAlert-icon': { color: qbColors.blue },
  '& .MuiAlert-action .MuiIconButton-root': { color: qbColors.blue },
};

// Column Filter Component
const ColumnFilter = ({ column, displayName, uniqueValues, selectedValues, onChange, onClear, isMobile }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 200 }}>
      <FormControl size="small" sx={{ minWidth: isMobile ? 80 : 150, maxWidth: isMobile ? 120 : 200 }}>
        <InputLabel sx={isMobile ? { fontSize: '0.75rem' } : {}}>{isMobile ? 'Filter' : `Filter ${displayName}`}</InputLabel>
        <Select
          multiple
          value={selectedValues || []}
          onChange={(e) => onChange(column, e.target.value)}
          input={<OutlinedInput label={isMobile ? 'Filter' : `Filter ${displayName}`} />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.slice(0, isMobile ? 1 : 2).map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
              {selected.length > (isMobile ? 1 : 2) && (
                <Chip 
                  label={`+${selected.length - (isMobile ? 1 : 2)} more`} 
                  size="small" 
                  variant="outlined" 
                />
              )}
            </Box>
          )}
          sx={{ fontSize: '0.875rem' }}
        >
          {uniqueValues.map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {selectedValues && selectedValues.length > 0 && (
        <Tooltip title="Clear filter">
          <IconButton 
            size="small" 
            onClick={() => onClear(column)}
            sx={{ p: 0.5 }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const TEAM_NAME_KEYS = new Set([
  'batting_team',
  'bowling_team',
  'team',
  'team1',
  'team2',
  'winner',
  'loser',
  'opposition',
  'opponent_team',
  'toss_winner',
]);

const PLAYER_NAME_KEYS = new Set([
  'batter',
  'bowler',
  'striker',
  'non_striker',
  'player',
  'player_name',
  'player_of_match',
  'captain',
  'vice_captain',
]);

const isTeamNameColumn = (key) => {
  const normalized = String(key || '').toLowerCase();
  return TEAM_NAME_KEYS.has(normalized) || normalized.endsWith('_team');
};

const isPlayerNameColumn = (key) => {
  const normalized = String(key || '').toLowerCase();
  return PLAYER_NAME_KEYS.has(normalized) || normalized.endsWith('_player');
};

const DEFAULT_SELECTED_METRIC_COLUMNS = ['balls', 'runs', 'strike_rate', 'impact'];

const PRIMER_METRIC_COLUMNS = new Set([
  'impact', 'impact_per_100', 'impact_per_innings', 'raa', 'raa_per_100', 'waa', 'waa_per_100', 'wpa', 'avg_leverage',
]);

// Bookkeeping fields that ride along with the T20 Primer metrics; never table columns.
const INTERNAL_COLUMNS = new Set(['is_summary', 'summary_level', 'metric_balls', 'metrics_perspective', 'coverage_excluded']);
// Tagged-ball counts (control_tagged...) feed the coverage % columns; the % is what people read.
const isInternalColumn = (col) => INTERNAL_COLUMNS.has(col) || col.endsWith('_tagged');

// Ranked by a tagged metric, rows whose balls are mostly untagged rank last, greyed: a 34%-tagged
// stand can't top a 99.9%-tagged one (utils/coverage, services/coverage.py).

// Columns worth offering: not a grouping key, not internal, and not empty in every row (the
// Primer metrics are null outside men's T20, and an all-blank Impact column is just noise).
const offerableColumns = (rows, groupBy) => {
  if (!rows.length) return [];
  return Object.keys(rows[0]).filter((col) => (
    !groupBy.includes(col)
    && !isInternalColumn(col)
    && rows.some((row) => row[col] !== null && row[col] !== undefined)
  ));
};

const buildInitialMetricColumns = ({ metricColumns, recommendedColumns }) => {

  if (metricColumns.length === 0) {
    return [];
  }

  const normalizedRecommended = Array.isArray(recommendedColumns) ? recommendedColumns : [];
  const preferred = normalizedRecommended
    .map(col => String(col || '').trim())
    .filter(Boolean)
    .filter((col, index, arr) => arr.indexOf(col) === index)
    .filter(col => metricColumns.includes(col))
    .slice(0, 6);

  if (preferred.length > 0) {
    return preferred;
  }

  const defaultColumns = DEFAULT_SELECTED_METRIC_COLUMNS.filter(col => metricColumns.includes(col));
  if (defaultColumns.length > 0) {
    return defaultColumns;
  }

  return metricColumns.slice(0, 3);
};

const normalizeRecommendedChart = (recommendedChart) => {
  if (!recommendedChart || typeof recommendedChart !== 'object') {
    return null;
  }

  const chartType = String(recommendedChart.type || '').trim().toLowerCase();
  if (!['bar', 'scatter', 'line'].includes(chartType)) {
    return null;
  }

  const xAxis = String(recommendedChart.x_axis || '').trim().toLowerCase();
  // y_axis can be a single column name or an array (for stacked / multi-bar).
  const rawY = recommendedChart.y_axis;
  const yAxis = Array.isArray(rawY)
    ? rawY.map((y) => String(y || '').trim().toLowerCase()).filter(Boolean)
    : String(rawY || '').trim().toLowerCase();
  const reason = String(recommendedChart.reason || '').trim();
  const series = recommendedChart.series
    ? String(recommendedChart.series).trim().toLowerCase()
    : null;
  const stacked = Boolean(recommendedChart.stacked);
  const secondaryYAxis = recommendedChart.secondary_y_axis
    ? String(recommendedChart.secondary_y_axis).trim().toLowerCase()
    : null;

  return {
    type: chartType,
    x_axis: xAxis,
    y_axis: yAxis,
    reason,
    stacked,
    series,
    secondary_y_axis: secondaryYAxis,
  };
};

const QueryResults = ({
  results,
  groupBy,
  filters,
  recommendedColumns,
  recommendedChart,
  nlInterpretation,
  nlConfidence,
  nlRawFilters,
  nlSourceQuery,
  onSuggestionClick,
  onDismissInterpretation,
  interpretationDisabled = false,
  isMobile,
  ballAggregation = 'snapshot',
  apiQueryString = '',
  onBallAggregationChange,
  autoOpenGraphic = false,
}) => {
  const ballAggregationApplicable = Array.isArray(groupBy) && (
    groupBy.includes('ball') || groupBy.includes('ball_in_spell')
  );
  const [page, setPage] = useState(0);
  const [graphicOpen, setGraphicOpen] = useState(false);
  const autoOpened = useRef(false);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [minCoverage, setMinCoverage] = useState(DEFAULT_MIN_COVERAGE);
  const coverageTag = TAGGED_METRICS[sortConfig.key] || null;
  const coverageColumn = coverageTag ? `${coverageTag}_coverage_pct` : null;
  
  // Column filtering state
  const [columnFilters, setColumnFilters] = useState({});
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterColumn, setFilterColumn] = useState(null);
  
  // Chart panel ref to trigger chart additions
  const chartPanelRef = useRef(null);
  const lastAutoChartKeyRef = useRef(null);
  const [showCharts, setShowCharts] = useState(false);
  const [showPitchMap, setShowPitchMap] = useState(false);
  const [aiChartReason, setAiChartReason] = useState(null);
  const [selectedMetricColumns, setSelectedMetricColumns] = useState(DEFAULT_SELECTED_METRIC_COLUMNS);
  const [defaultMetricColumns, setDefaultMetricColumns] = useState(DEFAULT_SELECTED_METRIC_COLUMNS);
  const [columnsAnchorEl, setColumnsAnchorEl] = useState(null);
  const [headerCollapsed, setHeaderCollapsed] = useState(isMobile);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSelection, setFeedbackSelection] = useState(null);
  const [feedbackNote, setFeedbackNote] = useState('');
  const [feedbackError, setFeedbackError] = useState(null);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  // Extract data early to avoid hooks rule violation
  const data = results?.data || [];
  const summaryData = results?.summary_data || null;
  const metadata = results?.metadata || {};
  const isGrouped = groupBy && groupBy.length > 0;
  const normalizedRecommendedChart = useMemo(
    () => normalizeRecommendedChart(recommendedChart),
    [recommendedChart]
  );
  const hasSummaries = metadata.has_summaries || false;
  const pitchMapMode = useMemo(() => getPitchMapMode(groupBy || []), [groupBy]);
  const resultRowCount = useMemo(() => {
    if (typeof metadata.returned_groups === 'number') {
      return metadata.returned_groups;
    }
    if (typeof metadata.total_matching_rows === 'number') {
      return metadata.total_matching_rows;
    }
    return Array.isArray(data) ? data.length : 0;
  }, [metadata, data]);
  
  // Add percent_balls to data (for pitch map and charts, without summary rows)
  const dataWithPercentBalls = useMemo(() => {
    if (!hasSummaries || !summaryData?.percentages || !isGrouped) {
      return data;
    }
    
    // Create a lookup for percentages
    const percentageLookup = {};
    summaryData.percentages.forEach(item => {
      const key = groupBy.map(col => String(item[col] || 'null')).join('|');
      percentageLookup[key] = item.percent_balls;
    });
    
    return data.map(row => {
      const key = groupBy.map(col => String(row[col] || 'null')).join('|');
      return {
        ...row,
        percent_balls: percentageLookup[key] || 0
      };
    });
  }, [data, summaryData, hasSummaries, isGrouped, groupBy]);
  
  // Merge regular data with summary data for table display
  const displayData = useMemo(() => {
    // Always add is_summary: false to regular data first
    const dataWithFlags = dataWithPercentBalls.map(row => ({
      ...row,
      is_summary: false
    }));
    
    // If no summaries requested, just return data with flags
    if (!hasSummaries || !summaryData || !isGrouped) {
      return dataWithFlags;
    }
    
    // If we have multi-level grouping, insert summary rows
    if (groupBy.length > 1 && summaryData[`${groupBy[0]}_summaries`]) {
      const summaries = summaryData[`${groupBy[0]}_summaries`];
      const mergedData = [];
      
      // Group data by first grouping column to insert summaries
      const groupedByFirst = {};
      dataWithFlags.forEach(row => {
        const firstGroupValue = String(row[groupBy[0]] || 'null');
        if (!groupedByFirst[firstGroupValue]) {
          groupedByFirst[firstGroupValue] = [];
        }
        groupedByFirst[firstGroupValue].push(row);
      });
      
      // Sort by the first group column to match order
      const sortedGroups = Object.keys(groupedByFirst).sort((a, b) => {
        if (!isNaN(a) && !isNaN(b)) {
          return Number(b) - Number(a);
        }
        return a.localeCompare(b);
      });
      
      sortedGroups.forEach(groupValue => {
        mergedData.push(...groupedByFirst[groupValue]);
        
        const summary = summaries.find(s => String(s[groupBy[0]]) === groupValue);
        if (summary) {
          const summaryRow = {
            ...summary,
            ...groupBy.slice(1).reduce((acc, col) => ({ ...acc, [col]: null }), {}),
            balls: summary.total_balls,
            runs: summary.total_runs,
            wickets: summary.total_wickets,
            dots: summary.total_dots,
            boundaries: summary.total_boundaries,
            fours: summary.total_fours,
            sixes: summary.total_sixes,
            average: summary.total_wickets > 0 ? summary.total_runs / summary.total_wickets : null,
            strike_rate: summary.total_balls > 0 ? (summary.total_runs * 100) / summary.total_balls : 0,
            balls_per_dismissal: summary.total_wickets > 0 ? summary.total_balls / summary.total_wickets : null,
            dot_percentage: summary.total_balls > 0 ? (summary.total_dots * 100) / summary.total_balls : 0,
            boundary_percentage: summary.total_balls > 0 ? (summary.total_boundaries * 100) / summary.total_balls : 0,
            percent_balls: 100.0,
            is_summary: true,
            summary_level: 1
          };
          mergedData.push(summaryRow);
        }
      });
      
      return mergedData;
    }
    
    return dataWithFlags;
  }, [dataWithPercentBalls, summaryData, hasSummaries, isGrouped, groupBy]);
  
  // Available metric columns for mobile column selector
  const availableMetricColumns = useMemo(() => {
    if (displayData.length === 0 || !isGrouped) return [];
    return offerableColumns(displayData, groupBy);
  }, [displayData, isGrouped, groupBy]);

  useEffect(() => {
    if (!isGrouped || displayData.length === 0) {
      return;
    }

    const initialColumns = buildInitialMetricColumns({
      metricColumns: offerableColumns(displayData, groupBy),
      recommendedColumns,
    });
    setSelectedMetricColumns(initialColumns);
    setDefaultMetricColumns(initialColumns);
  }, [results, isGrouped, displayData, groupBy, recommendedColumns]);

  const autoChartRecommendation = useMemo(() => {
    if (!normalizedRecommendedChart || !isGrouped) {
      return null;
    }

    const rowCount = Array.isArray(data) ? data.length : 0;
    if (rowCount < 3 || rowCount > 50) {
      return null;
    }

    return normalizedRecommendedChart;
  }, [normalizedRecommendedChart, isGrouped, data]);

  const autoChartRecommendationKey = useMemo(() => {
    if (!autoChartRecommendation) {
      return null;
    }
    const rowCount = Array.isArray(data) ? data.length : 0;
    return [
      autoChartRecommendation.type,
      autoChartRecommendation.x_axis || '',
      autoChartRecommendation.y_axis || '',
      groupBy.join('|'),
      rowCount,
      JSON.stringify(metadata.filters_applied || {}),
    ].join('::');
  }, [autoChartRecommendation, groupBy, data, metadata]);

  const initialChartRecommendation = useMemo(() => {
    if (!autoChartRecommendation || !autoChartRecommendationKey) {
      return null;
    }
    return {
      ...autoChartRecommendation,
      key: autoChartRecommendationKey,
    };
  }, [autoChartRecommendation, autoChartRecommendationKey]);

  useEffect(() => {
    if (!initialChartRecommendation) {
      setAiChartReason(null);
      return;
    }
    if (lastAutoChartKeyRef.current === initialChartRecommendation.key) {
      return;
    }

    lastAutoChartKeyRef.current = initialChartRecommendation.key;
    setShowCharts(true);
    setAiChartReason(
      initialChartRecommendation.reason ||
      'it best fits the grouped metrics returned for this query'
    );
  }, [initialChartRecommendation]);

  useEffect(() => {
    setFeedbackSubmitting(false);
    setFeedbackSelection(null);
    setFeedbackNote('');
    setFeedbackError(null);
    setFeedbackSuccess(false);
  }, [nlSourceQuery, metadata.total_matching_rows, metadata.returned_groups]);

  // From /graphics: open the graphic maker once, the first time a grouped result arrives.
  useEffect(() => {
    if (autoOpenGraphic && !autoOpened.current && isGrouped && data.length > 0) {
      autoOpened.current = true;
      setGraphicOpen(true);
    }
  }, [autoOpenGraphic, isGrouped, data.length]);

  // Apply column filters to displayData
  const filteredData = useMemo(() => {
    if (!columnFilters || Object.keys(columnFilters).length === 0) {
      return displayData;
    }
    
    return displayData.filter(row => {
      return Object.entries(columnFilters).every(([column, selectedValues]) => {
        if (!selectedValues || selectedValues.length === 0) return true;
        
        const cellValue = row[column];
        const valueStr = cellValue === null || cellValue === undefined ? 'N/A' : String(cellValue);
        
        return selectedValues.includes(valueStr);
      });
    });
  }, [displayData, columnFilters]);
  
  // Memoized sorted data
  const sortedData = useMemo(() => {
    if (!sortConfig.key || filteredData.length === 0) return filteredData;

    const sorted = [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    // Under the floor: kept and shown, but ranked after every row that qualifies.
    return rankWithCoverage(sorted, sortConfig.key, minCoverage);
  }, [filteredData, sortConfig, minCoverage]);
  
  // Early return after all hooks
  if (!results || !results.data) {
    return (
      <Alert severity="info">
        No results to display. Execute a query to see results here.
      </Alert>
    );
  }
  
  // Chart management functions
  const handleAddBarChart = () => {
    setShowCharts(true);
    setTimeout(() => {
      if (chartPanelRef.current && chartPanelRef.current.addBarChart) {
        chartPanelRef.current.addBarChart();
      }
    }, 100);
  };

  const handleAddScatterChart = () => {
    setShowCharts(true);
    setTimeout(() => {
      if (chartPanelRef.current && chartPanelRef.current.addScatterChart) {
        chartPanelRef.current.addScatterChart();
      }
    }, 100);
  };

  const handleAddLineChart = () => {
    setShowCharts(true);
    setTimeout(() => {
      if (chartPanelRef.current && chartPanelRef.current.addLineChart) {
        chartPanelRef.current.addLineChart();
      }
    }, 100);
  };

  // Sorting logic
  const handleSort = (columnKey) => {
    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnKey, direction });
    setPage(0);
  };
  
  // Column filtering logic
  const getUniqueValuesForColumn = (column) => {
    const values = new Set();
    displayData.forEach(row => {
      const value = row[column];
      const valueStr = value === null || value === undefined ? 'N/A' : String(value);
      values.add(valueStr);
    });
    return Array.from(values).sort();
  };
  
  const handleColumnFilter = (column, selectedValues) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: selectedValues
    }));
    setPage(0);
  };
  
  const clearColumnFilter = (column) => {
    setColumnFilters(prev => {
      const updated = { ...prev };
      delete updated[column];
      return updated;
    });
    setPage(0);
  };
  
  const clearAllFilters = () => {
    setColumnFilters({});
    setPage(0);
  };

  const handleFilterClick = (event, column) => {
    setFilterAnchorEl(event.currentTarget);
    setFilterColumn(column);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setFilterColumn(null);
  };

  const isFilterableColumn = (column) => {
    return groupBy.includes(column) ||
      ['crease_combo', 'ball_direction', 'bowl_style', 'bowl_kind', 'striker_batter_type', 'non_striker_batter_type', 'venue', 'batting_team', 'bowling_team', 'batter', 'bowler', 'competition'].includes(column);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const exportToCSV = () => {
    if (!sortedData || sortedData.length === 0) return;
    
    const exportData = sortedData;
    const headers = Object.keys(exportData[0]);
    
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => 
        headers.map(header => {
          const value = row[header];
          if (value === null || value === undefined) return '';
          if (typeof value === 'string' && value.includes(',')) {
            return `"${value.replace(/"/g, '""')}"`;  
          }
          return value;
        }).join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    const filterSuffix = Object.keys(columnFilters).length > 0 ? '-filtered' : '';
    const filename = `cricket-query${filterSuffix}-${new Date().toISOString().split('T')[0]}.csv`;
    link.setAttribute('download', filename);
    
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSummarize = async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const contextDesc = nlSourceQuery
        || Object.entries(filters || {})
            .filter(([, v]) => v != null && v !== '' && !(Array.isArray(v) && v.length === 0))
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
            .join('; ')
        || 'Query results';
      const response = await axios.post(`${config.API_URL}/summarize/results`, {
        context_description: contextDesc.slice(0, 500),
        filters: filters || {},
        group_by: groupBy || [],
        result_data: (data || []).slice(0, 50),
        result_count: data?.length || 0,
      });
      if (response.data.success) {
        setAiSummary(response.data.summary);
      } else {
        setSummaryError(response.data.error || 'Failed to generate summary');
      }
    } catch (err) {
      setSummaryError(apiErrorText(err, null) || err.message || 'Failed to generate summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  const submitFeedback = async ({ feedback, refinedQueryText = null }) => {
    const normalizedQuery = String(nlSourceQuery || '').trim();
    if (!normalizedQuery) {
      setFeedbackError('Feedback is available only for natural-language queries.');
      return false;
    }

    try {
      setFeedbackSubmitting(true);
      setFeedbackError(null);

      const payload = {
        query_text: normalizedQuery,
        feedback,
        execution_success: true,
        result_row_count: Number.isInteger(resultRowCount) ? resultRowCount : 0,
      };

      if (refinedQueryText) {
        payload.refined_query_text = refinedQueryText;
      }

      await axios.post(`${config.API_URL}/nl2query/feedback`, payload);
      setFeedbackSuccess(true);
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail;
      setFeedbackError(typeof detail === 'string' ? detail : 'Failed to submit feedback. Please try again.');
      return false;
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const handleThumbsUp = async () => {
    setFeedbackSelection('good');
    const ok = await submitFeedback({ feedback: 'good' });
    if (!ok) {
      setFeedbackSelection(null);
    }
  };

  const handleThumbsDown = () => {
    setFeedbackSelection('bad');
    setFeedbackSuccess(false);
    setFeedbackError(null);
  };

  const handleBadFeedbackSubmit = async () => {
    const note = feedbackNote.trim();
    const ok = await submitFeedback({
      feedback: 'bad',
      refinedQueryText: note || null,
    });
    if (!ok) {
      return;
    }
    setFeedbackSelection('submitted');
  };
  
  const formatValue = (value, key) => {
    if (value === null || value === undefined) return 'N/A';

    if (typeof value === 'string' && isTeamNameColumn(key)) {
      const normalized = value.trim().toLowerCase();
      if (['no result', 'tie', 'draw', 'abandoned', 'n/a'].includes(normalized)) {
        return value;
      }
      return <CondensedName name={value} type="team" />;
    }

    if (typeof value === 'string' && isPlayerNameColumn(key)) {
      return <CondensedName name={value} type="player" />;
    }

    if (key.includes('percentage') || key === 'percent_balls') {
      return `${Number(value).toFixed(1)}%`;
    }
    if (key === 'strike_rate') {
      return Number(value).toFixed(1);
    }
    if (key === 'average' || key === 'balls_per_dismissal') {
      return Number(value).toFixed(2);
    }
    if (key === 'economy' || key === 'overs' || key === 'balls_per_wicket') {
      return Number(value).toFixed(2);
    }
    if (key === 'year') {
      return value;
    }
    if (typeof value === 'number' && value > 1000) {
      return value.toLocaleString();
    }
    
    return value;
  };
  
  const getColumnDisplayName = (key) => {
    const displayNames = {
      'match_id': 'Match',
      'crease_combo': 'Crease',
      'ball_direction': 'Direction',
      'bowl_style': 'Style',
      'bowl_kind': 'Kind',
      'striker_batter_type': 'Striker',
      'non_striker_batter_type': 'Non-Striker',
      'runs_off_bat': 'Runs',
      'batting_team': 'Bat Team',
      'bowling_team': 'Bowl Team',
      'strike_rate': 'SR',
      'dot_percentage': 'Dot%',
      'boundary_percentage': 'Bndry%',
      'percent_balls': '%Balls',
      'balls_per_dismissal': 'B/W',
      'control_percentage': 'Ctrl%',
      'control_coverage_pct': 'Ctrl data%',
      'shot_coverage_pct': 'Shot data%',
      'line_coverage_pct': 'Line data%',
      'length_coverage_pct': 'Length data%',
      'wagon_zone_coverage_pct': 'Zone data%',
      'year': 'Year',
      'runs_conceded': 'Runs',
      'fours_conceded': 'Fours',
      'sixes_conceded': 'Sixes',
      'economy': 'Economy',
      'balls_per_wicket': 'B/W',
      'overs': 'Overs',
      'innings_count': 'Innings',
      'balls_faced': 'Balls',
      // T20 Primer metrics (see Credits / the Primer): runs added, runs & wickets above average,
      // win probability added, and how much was at stake on the average ball.
      'impact': 'Impact',
      'impact_per_100': 'Imp/100',
      'impact_per_innings': 'Imp/Inns',
      'raa': 'RAA',
      'raa_per_100': 'RAA/100',
      'waa': 'WAA',
      'waa_per_100': 'WAA/100',
      'wpa': 'WPA',
      'avg_leverage': 'Leverage',
    };
    
    return displayNames[key] || key.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };
  
  const getGroupingColumns = () => {
    if (!isGrouped || displayData.length === 0) return [];
    
    const sampleRow = displayData[0];
    return Object.keys(sampleRow).filter(key => groupBy.includes(key));
  };
  
  const getVisibleColumns = () => {
    if (displayData.length === 0) return [];
    
    const allColumns = Object.keys(displayData[0]);
    
    if (isGrouped) {
      const cols = [...getGroupingColumns(), ...selectedMetricColumns];
      // Coverage sits beside a tagged metric being ranked, and beside any tag filter's counts.
      const coverageCols = allColumns.filter((c) => c.endsWith('_coverage_pct')
        && (c === coverageColumn || (c !== 'control_coverage_pct')));
      coverageCols.forEach((c) => { if (!cols.includes(c)) cols.push(c); });
      return cols.filter(col => allColumns.includes(col));
    }

    if (isMobile) {
      return ['batter', 'bowler', 'runs_off_bat', 'crease_combo'].filter(col => allColumns.includes(col));
    }
    
    return allColumns.filter(col => !['is_summary', 'summary_level'].includes(col));
  };
  
  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const visibleColumns = getVisibleColumns();
  // The first grouping column stays pinned while the rest scroll. On a phone a long label
  // ("Rohit Sharma & Shubman Gill") made it ~270 of 360px, leaving no room to scroll the
  // numbers into view: cap it and let the label wrap. The shadow marks the pinned edge.
  const pinnedColumnSx = isMobile ? {
    width: '38vw',
    maxWidth: '38vw',
    minWidth: 96,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    lineHeight: 1.25,
    boxShadow: `inset -1px 0 0 ${qbColors.borderStrong}, 6px 0 8px -6px rgba(0,0,0,0.6)`,
  } : {};
  const hiddenMetricCount = Math.max(availableMetricColumns.length - selectedMetricColumns.length, 0);
  const columnsMenuOpen = Boolean(columnsAnchorEl);

  return (
    <Box sx={{ color: qbColors.textHi, width: '100%', minWidth: 0, overflow: 'hidden' }}>
      {/* Results Summary */}
      <Card sx={{ ...qbCardSx, mb: 2.5, maxWidth: '100%', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 1.6, md: 2 }, pb: headerCollapsed ? '12px !important' : undefined }}>
          {/* Compact summary pill — always visible */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              cursor: 'pointer',
              minWidth: 0,
            }}
            onClick={() => setHeaderCollapsed(prev => !prev)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontFamily: qbFonts.display, fontSize: headerCollapsed ? '1rem' : 23, fontWeight: 700, flexShrink: 0 }}>
                Query Results
              </Typography>
              {headerCollapsed && (
                <>
                  <Chip
                    label={isGrouped
                      ? `${metadata.total_groups || metadata.returned_groups} rows`
                      : `${(metadata.total_matching_rows || 0).toLocaleString()} deliveries`}
                    size="small"
                    sx={{ bgcolor: qbColors.accent, color: qbColors.bg, fontWeight: 700 }}
                  />
                  {isGrouped && (
                    <Chip
                      label={`Grouped by ${groupBy.join(', ')}`}
                      variant="outlined"
                      size="small"
                      sx={darkOutlineChipSx}
                    />
                  )}
                </>
              )}
            </Box>
              <IconButton size="small" sx={{ color: qbColors.textLo }}>
              {headerCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          </Box>

          {/* Full header content — collapsible */}
          <Collapse in={!headerCollapsed}>
            <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="flex-start" sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={7}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {isGrouped ? (
                    <>
                      <Chip
                        label={`${metadata.total_groups || metadata.returned_groups} groups`}
                        size="small"
                        sx={{ bgcolor: qbColors.accent, color: qbColors.bg, fontWeight: 700 }}
                      />
                      <Chip
                        label={`Grouped by: ${groupBy.join(', ')}`}
                        variant="outlined"
                        size="small"
                        sx={{ ...darkOutlineChipSx, maxWidth: { xs: '100%', md: 360 } }}
                      />
                      {hasSummaries && (
                        <Chip
                          label="With Summary Rows"
                          size="small"
                          sx={{ bgcolor: 'rgba(201,156,240,0.16)', color: qbColors.purple, fontWeight: 700 }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <Chip
                        label={`${(metadata.total_matching_rows || 0).toLocaleString()} total deliveries`}
                        size="small"
                        sx={{ bgcolor: qbColors.accent, color: qbColors.bg, fontWeight: 700 }}
                      />
                      <Chip
                        label={`Showing ${sortedData.length}`}
                        variant="outlined"
                        size="small"
                        sx={darkOutlineChipSx}
                      />
                    </>
                  )}

                  {metadata.total_innings_in_query !== undefined && metadata.total_innings_in_query !== null && (
                    <Chip
                      label={`${Number(metadata.total_innings_in_query).toLocaleString()} total innings`}
                      variant="outlined"
                      size="small"
                      sx={darkOutlineChipSx}
                    />
                  )}

                  {sortConfig.key && (
                    <Chip
                      label={`Sorted by: ${getColumnDisplayName(sortConfig.key)} (${sortConfig.direction})`}
                      variant="outlined"
                      size="small"
                      sx={{ ...darkOutlineChipSx, color: qbColors.purple, borderColor: 'rgba(201,156,240,0.45)' }}
                    />
                  )}

                  {coverageTag && (
                    // Ranked by a tagged metric: rows under this share of tagged balls rank last, greyed.
                    <TextField
                      id="qb-min-coverage"
                      size="small"
                      type="number"
                      label={`Min ${coverageTag} data %`}
                      value={minCoverage}
                      onChange={(e) => setMinCoverage(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                      inputProps={{ min: 0, max: 100, step: 5, 'aria-describedby': 'qb-min-coverage-help' }}
                      helperText={`${sortedData.filter((r) => r.coverage_excluded).length} not ranked`}
                      FormHelperTextProps={{ id: 'qb-min-coverage-help' }}
                      sx={{ width: 150 }}
                    />
                  )}

                  {Object.keys(columnFilters).length > 0 && (
                    <Chip
                      label={`${Object.keys(columnFilters).length} column filter${Object.keys(columnFilters).length > 1 ? 's' : ''} active`}
                      size="small"
                      icon={<FilterListIcon />}
                      sx={{ bgcolor: 'rgba(240,180,41,0.14)', color: qbColors.gold, '& .MuiChip-icon': { color: qbColors.gold } }}
                    />
                  )}
                </Box>

                {metadata.filters_applied && (
                  <Typography variant="body2" sx={{ color: qbColors.textFaint }}>
                    {metadata.note}
                  </Typography>
                )}

                {aiChartReason && (
                  <Alert
                    severity="info"
                    sx={{ mt: 1.25, ...darkInfoAlertSx }}
                    onClose={() => setAiChartReason(null)}
                  >
                    {`AI suggested this chart because: ${aiChartReason}`}
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12} md={5} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'stretch', md: 'flex-end' },
                    '& .MuiButton-root': {
                      flex: { xs: '1 1 150px', sm: '0 1 auto' },
                      minWidth: { xs: 0, sm: 'auto' },
                    },
                  }}
                >
                  {isGrouped && data.length > 0 && Object.keys(columnFilters).length > 0 && (
                    <Button
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={clearAllFilters}
                      size="small"
                      sx={{ ...qbGhostButtonSx, color: qbColors.gold, borderColor: 'rgba(240,180,41,0.35)' }}
                    >
                      Clear Filters
                    </Button>
                  )}

                  {isGrouped && pitchMapMode && data.length > 0 && (
                    <Button
                      variant={showPitchMap ? "contained" : "outlined"}
                      startIcon={<MapIcon />}
                      onClick={() => setShowPitchMap(!showPitchMap)}
                      size="small"
                      sx={showPitchMap ? { ...qbButtonSx, minHeight: 36, fontSize: 11 } : { ...qbGhostButtonSx, minHeight: 36 }}
                    >
                      {showPitchMap ? 'Hide Pitch Map' : 'Show Pitch Map'}
                    </Button>
                  )}

                  {isGrouped && data.length > 0 && (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={() => setGraphicOpen(true)}
                        size="small"
                        sx={{ ...qbButtonSx, minHeight: 36, fontSize: 11 }}
                      >
                        Make graphic
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddBarChart}
                        size="small"
                        sx={{ ...qbButtonSx, minHeight: 36, fontSize: 11 }}
                      >
                        Bar chart
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddScatterChart}
                        size="small"
                        sx={{ ...qbButtonSx, minHeight: 36, fontSize: 11 }}
                      >
                        Scatter plot
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleAddLineChart}
                        size="small"
                        sx={{ ...qbGhostButtonSx, minHeight: 36 }}
                      >
                        Line chart
                      </Button>
                    </>
                  )}
                  {isGrouped && availableMetricColumns.length > 0 && (
                    <Button
                      variant="outlined"
                      onClick={(event) => setColumnsAnchorEl(event.currentTarget)}
                      size="small"
                      sx={{
                        ...qbGhostButtonSx,
                        minHeight: 36,
                        borderColor: hiddenMetricCount > 0 ? qbColors.accent : qbColors.borderStrong,
                      }}
                    >
                      Columns
                      <Box
                        component="span"
                        sx={{
                          ml: 0.75,
                          px: 0.65,
                          py: 0.1,
                          borderRadius: '999px',
                          bgcolor: qbColors.accent,
                          color: qbColors.bg,
                          fontSize: 10,
                          fontWeight: 700,
                        }}
                      >
                        {visibleColumns.length}
                      </Box>
                    </Button>
                  )}
                  <ShareButton kind="query" sx={{ ...qbGhostButtonSx }} />
                  {apiQueryString && groupBy?.length > 0 && (
                    <ChartExportButton
                      sx={{ ...qbGhostButtonSx }}
                      request={() => ({
                        kind: 'query',
                        query_string: apiQueryString,
                        params: sortConfig.key
                          ? { sort_by: sortConfig.key, sort_descending: sortConfig.direction === 'desc' }
                          : {},
                      })}
                    />
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<GetAppIcon />}
                    onClick={exportToCSV}
                    disabled={!data || data.length === 0}
                    size="small"
                    sx={{ ...qbGhostButtonSx, minHeight: 36 }}
                  >
                    Export CSV
                  </Button>
                  <Button
                    variant={aiSummary ? 'outlined' : 'contained'}
                    startIcon={summaryLoading ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                    onClick={handleSummarize}
                    disabled={summaryLoading || !data || data.length === 0}
                    size="small"
                    sx={aiSummary ? { ...qbGhostButtonSx, minHeight: 36 } : { ...qbButtonSx, minHeight: 36, fontSize: 11 }}
                  >
                    {summaryLoading ? 'Analyzing...' : aiSummary ? 'Re-analyze' : 'Summarize'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>
      
      {/* Pitch Map Visualization - use dataWithPercentBalls which has percent_balls */}
      {showPitchMap && pitchMapMode && isGrouped && (
        <Box sx={{ mb: 3 }}>
          <PitchMapContainer
            data={dataWithPercentBalls}
            groupBy={groupBy}
            filters={filters || metadata?.filters_applied || {}}
          />
        </Box>
      )}
      
      {/* AI Summary */}
      {(aiSummary || summaryError) && (
        <Card variant="outlined" sx={{ ...qbCardSx, mb: 2, bgcolor: qbColors.surface2 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                <AutoAwesomeIcon fontSize="small" sx={{ color: qbColors.accent }} />
                <Typography variant="subtitle2" sx={{ color: qbColors.textHi, fontFamily: qbFonts.display }}>AI Summary</Typography>
              </Stack>
              <IconButton size="small" onClick={() => { setAiSummary(null); setSummaryError(null); }} sx={{ color: qbColors.textLo }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
            {summaryError ? (
              <Alert severity="error" sx={{ py: 0 }}>{summaryError}</Alert>
            ) : (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6, color: qbColors.textMed }}>
                {aiSummary}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Data Table with Sorting */}
      {isGrouped && visibleColumns.some((col) => PRIMER_METRIC_COLUMNS.has(col)) && (
        <Typography sx={{ color: qbColors.textLo, fontSize: 12, mb: 1, lineHeight: 1.5 }}>
          <b style={{ color: qbColors.textMed }}>Impact</b> = runs added to the team&apos;s projected total ·{' '}
          <b style={{ color: qbColors.textMed }}>RAA / WAA</b> = runs and wickets above average for the game state ·{' '}
          <b style={{ color: qbColors.textMed }}>WPA</b> = win probability added. Men&apos;s T20 only,{' '}
          {displayData[0]?.metrics_perspective === 'bowling' ? 'from the bowling side' : 'from the batting side'}; method from
          Himanish Ganjoo&apos;s <i>T20 Metrics: A Primer</i>.
        </Typography>
      )}
      <Paper sx={{ ...qbCardSx, overflow: 'hidden', width: '100%', maxWidth: '100%' }}>
        <ScrollTable sx={{ maxHeight: isMobile ? 400 : 600, bgcolor: qbColors.surface1, overflowX: 'auto' }}>
          <Table stickyHeader size={isMobile ? "small" : "medium"} sx={{ minWidth: isMobile ? visibleColumns.length * 84 : Math.max(visibleColumns.length * 124, 760) }}>
            <TableHead>
              <TableRow>
                {visibleColumns.map((column, colIndex) => {
                  const groupByIndex = groupBy.indexOf(column);
                  const isGroupByCol = groupByIndex !== -1;
                  const hasActiveFilter = columnFilters[column] && columnFilters[column].length > 0;
                  const filterable = isGrouped && isFilterableColumn(column);
                  return (
                  <TableCell key={column} sx={{
                    fontWeight: 600,
                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                    whiteSpace: 'nowrap',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    color: qbColors.textLo,
                    bgcolor: `${qbColors.surface2} !important`,
                    py: isMobile ? 0.5 : 1,
                    px: isMobile ? 0.75 : 1.5,
                    ...(isGroupByCol && {
                      position: 'sticky',
                      left: groupByIndex === 0 ? 0 : 'auto',
                      backgroundColor: `${qbColors.surface2} !important`,
                      zIndex: groupByIndex === 0 ? 3 : 1,
                    }),
                    ...(groupByIndex === 0 && pinnedColumnSx),
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                      <TableSortLabel
                        active={sortConfig.key === column}
                        direction={sortConfig.key === column ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort(column)}
                        hideSortIcon={sortConfig.key !== column}
                        sx={{
                          fontSize: 'inherit',
                          '& .MuiTableSortLabel-icon': {
                            opacity: sortConfig.key === column ? 1 : 0,
                            fontSize: '0.875rem',
                          },
                          '&:hover .MuiTableSortLabel-icon': {
                            opacity: 0.5
                          },
                          '&:hover': {
                            color: qbColors.accent
                          }
                        }}
                      >
                        {getColumnDisplayName(column)}
                      </TableSortLabel>
                      {filterable && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleFilterClick(e, column)}
                          sx={{
                            p: 0.25,
                            ml: 0.25,
                            color: hasActiveFilter ? qbColors.gold : qbColors.textGhost,
                            '&:hover': { color: hasActiveFilter ? qbColors.gold : qbColors.accent },
                          }}
                        >
                          <FilterListIcon sx={{ fontSize: '0.875rem' }} />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <Tooltip
                  key={index}
                  disableHoverListener={!row.coverage_excluded}
                  disableFocusListener={!row.coverage_excluded}
                  disableTouchListener={!row.coverage_excluded}
                  title={row.coverage_excluded
                    ? `Not ranked: ${row[coverageColumn] ?? 0}% of these balls have ${coverageTag} data (minimum ${minCoverage}%).`
                    : ''}
                  placement="top"
                >
                <TableRow 
                  hover={!row.is_summary}
                  aria-disabled={row.coverage_excluded || undefined}
                  sx={{
                    opacity: row.coverage_excluded ? 0.45 : 1,
                    backgroundColor: row.is_summary ? qbColors.surface2 : qbColors.surface1,
                    fontWeight: row.is_summary ? 'bold' : 'normal',
                    '&:hover .MuiTableCell-root': {
                      backgroundColor: row.is_summary ? qbColors.surface2 : `${qbColors.surface3} !important`,
                    },
                    '& .MuiTableCell-root': {
                      fontWeight: row.is_summary ? 'bold' : 'normal',
                      borderTop: row.is_summary ? '2px solid' : 'none',
                      borderTopColor: row.is_summary ? qbColors.borderStrong : 'transparent',
                      backgroundColor: row.is_summary ? qbColors.surface2 : qbColors.surface1,
                      color: qbColors.textMed,
                    }
                  }}
                >
                  {visibleColumns.map((column) => {
                    const groupByIndex = groupBy.indexOf(column);
                    const isGroupByCol = groupByIndex !== -1;
                    return (
                    <TableCell key={column} sx={{
                      whiteSpace: 'nowrap',
                      ...(isGroupByCol && {
                        position: 'sticky',
                        left: groupByIndex === 0 ? 0 : 'auto',
                        backgroundColor: row.is_summary ? `${qbColors.surface2} !important` : `${qbColors.surface1} !important`,
                        color: qbColors.textHi,
                        fontWeight: 700,
                        zIndex: groupByIndex === 0 ? 1 : 0,
                      }),
                      ...(groupByIndex === 0 && pinnedColumnSx),
                    }}>
                      {row.is_summary && column !== groupBy[0] && groupBy.includes(column) ?
                        '— Total —' :
                        column === 'partnership' && isMobile && typeof row[column] === 'string'
                          // "Rohit Sharma &" / "Shubman Gill": wrap at the pair, not mid-name.
                          ? row[column].split(' & ').map((name, i, all) => (
                            <React.Fragment key={name}>{name}{i < all.length - 1 ? '\u00a0&' : ''}{i < all.length - 1 && <br />}</React.Fragment>
                          ))
                          : formatValue(row[column], column)
                      }
                    </TableCell>
                    );
                  })}
                </TableRow>
                </Tooltip>
              ))}
            </TableBody>
          </Table>
        </ScrollTable>
        
        <TablePagination
          rowsPerPageOptions={isMobile ? [5, 10, 25] : [5, 10, 25, 50]}
          component="div"
          count={sortedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            bgcolor: qbColors.surface2,
            color: qbColors.textLo,
            borderTop: `1px solid ${qbColors.border}`,
            '& .MuiToolbar-root': {
              flexWrap: 'wrap',
              rowGap: 0.5,
              px: { xs: 1, sm: 2 },
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              color: qbColors.textLo,
              fontFamily: qbFonts.mono,
              fontSize: 11,
            },
            '& .MuiTablePagination-select, & .MuiSvgIcon-root': {
              color: qbColors.textMed,
            },
            '& .MuiIconButton-root': {
              color: qbColors.textMed,
              '&.Mui-disabled': { color: qbColors.textGhost },
            },
          }}
        />
      </Paper>

      <Menu
        open={columnsMenuOpen}
        anchorEl={columnsAnchorEl}
        onClose={() => setColumnsAnchorEl(null)}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 260,
              maxWidth: 'calc(100vw - 24px)',
              maxHeight: 360,
              bgcolor: qbColors.surface3,
              color: qbColors.textHi,
              border: `1px solid ${qbColors.borderStrong}`,
              borderRadius: '12px',
              boxShadow: '0 24px 48px -18px rgba(0,0,0,0.75)',
            }
          }
        }}
      >
        <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ fontFamily: qbFonts.mono, color: qbColors.textLo, fontSize: 10, letterSpacing: '0.12em' }}>
              COLUMNS
            </Typography>
            <Chip label="AI-picked" size="small" sx={{ height: 20, bgcolor: qbColors.accentSoft, color: qbColors.accent, fontFamily: qbFonts.mono, fontSize: 10 }} />
          </Box>
          <Button
            size="small"
            onClick={() => setSelectedMetricColumns(defaultMetricColumns)}
            sx={{ minWidth: 0, p: 0, color: qbColors.accent, fontFamily: qbFonts.mono, fontSize: 11 }}
          >
            Reset
          </Button>
        </Box>
        {getGroupingColumns().map((column) => (
          <MenuItem key={`locked-${column}`} disabled sx={{ opacity: '1 !important', minHeight: 34 }}>
            <Checkbox checked disabled size="small" sx={{ color: qbColors.accent, p: 0.5 }} />
            <ListItemText
              primary={getColumnDisplayName(column)}
              secondary="KEY"
              primaryTypographyProps={{ sx: { color: qbColors.textHi, fontSize: 13 } }}
              secondaryTypographyProps={{ sx: { color: qbColors.accent, fontFamily: qbFonts.mono, fontSize: 9 } }}
            />
          </MenuItem>
        ))}
        {availableMetricColumns.map((column) => (
          <MenuItem
            key={`metric-${column}`}
            onClick={() => {
              setSelectedMetricColumns((prev) =>
                prev.includes(column)
                  ? prev.filter((item) => item !== column)
                  : [...prev, column]
              );
            }}
            sx={{ minHeight: 34, color: qbColors.textMed }}
          >
            <Checkbox
              checked={selectedMetricColumns.includes(column)}
              size="small"
              sx={{
                p: 0.5,
                color: qbColors.textFaint,
                '&.Mui-checked': { color: qbColors.accent },
              }}
            />
            <ListItemText primary={getColumnDisplayName(column)} primaryTypographyProps={{ sx: { fontSize: 13 } }} />
          </MenuItem>
        ))}
      </Menu>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1, minWidth: 200 } } }}
      >
        {filterColumn && (
          <ColumnFilter
            column={filterColumn}
            displayName={getColumnDisplayName(filterColumn)}
            uniqueValues={getUniqueValuesForColumn(filterColumn)}
            selectedValues={columnFilters[filterColumn]}
            onChange={handleColumnFilter}
            onClear={clearColumnFilter}
            isMobile={isMobile}
          />
        )}
      </Popover>

      {/* Ball-aggregation toggle: only meaningful when grouping by ball or
          ball_in_spell. Snapshot = stats AT ball N. Cumulative = average
          running totals through ball N. */}
      {ballAggregationApplicable && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ color: qbColors.textLo }}>
            Ball aggregation:
          </Typography>
          <ToggleButtonGroup
            value={ballAggregation}
            exclusive
            size="small"
            onChange={(_, value) => {
              if (value && onBallAggregationChange) onBallAggregationChange(value);
            }}
          >
            <ToggleButton value="snapshot">Per-ball snapshot</ToggleButton>
            <ToggleButton value="cumulative">Cumulative</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {graphicOpen && (
        <GraphicMaker
          open={graphicOpen}
          onClose={() => setGraphicOpen(false)}
          apiQueryString={apiQueryString}
          rows={sortedData.filter((row) => !row.is_summary)}
          minCoverage={minCoverage}
          groupBy={groupBy || []}
          // A plain-English query already said what it is about: default to the parser's metric.
          defaultMetric={recommendedChart?.y_axis || sortConfig.key}
          scatter={recommendedChart?.type === 'scatter' ? recommendedChart : null}
        />
      )}

      {/* Chart Panel */}
      <ChartPanel
        ref={chartPanelRef}
        data={sortedData.filter(row => !row.is_summary)}
        groupBy={groupBy}
        initialRecommendation={initialChartRecommendation}
        isVisible={showCharts && isGrouped}
        onToggle={() => setShowCharts(!showCharts)}
        isMobile={isMobile}
      />
      
      {/* Applied Filters Info */}
      {metadata.filters_applied && (
        <Accordion sx={{ mt: 2, ...qbCardSx, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: qbColors.textLo }} />}>
            <Typography variant="h6" sx={{ color: qbColors.textHi, fontFamily: qbFonts.display }}>Applied Filters</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1}>
              {Object.entries(metadata.filters_applied).map(([key, value]) => {
                if (!value || (Array.isArray(value) && value.length === 0)) return null;
                
                return (
                  <Grid item key={key}>
                    <Chip 
                      label={`${getColumnDisplayName(key)}: ${Array.isArray(value) ? value.join(', ') : value}`}
                      variant="outlined"
                      size="small"
                      sx={darkOutlineChipSx}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {nlInterpretation && (
        <Box sx={{ mt: 2 }}>
          <NLInterpretation
            interpretation={nlInterpretation}
            confidence={nlConfidence}
            rawFilters={nlRawFilters}
            onSuggestionClick={onSuggestionClick}
            onClose={onDismissInterpretation}
            disabled={interpretationDisabled}
          />
        </Box>
      )}

      {String(nlSourceQuery || '').trim() && (
        <Paper
          elevation={0}
          sx={{
            mt: 2,
            p: 1.5,
            border: 1,
            borderColor: qbColors.border,
            bgcolor: qbColors.surface1,
            color: qbColors.textMed,
            borderRadius: '16px',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: qbColors.textMed }}>
              Was this helpful?
            </Typography>
            <IconButton
              size="small"
              onClick={handleThumbsUp}
              disabled={feedbackSubmitting || feedbackSuccess}
              sx={{ color: feedbackSelection === 'good' || feedbackSuccess ? qbColors.accent : qbColors.textLo }}
              aria-label="Helpful"
            >
              <ThumbUpAltOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleThumbsDown}
              disabled={feedbackSubmitting || feedbackSuccess}
              sx={{ color: feedbackSelection === 'bad' ? qbColors.red : qbColors.textLo }}
              aria-label="Not helpful"
            >
              <ThumbDownAltOutlinedIcon fontSize="small" />
            </IconButton>
          </Stack>

          {feedbackSelection === 'bad' && !feedbackSuccess && (
            <Box sx={{ mt: 1 }}>
              <TextField
                size="small"
                fullWidth
                label="What went wrong?"
                placeholder="e.g. wrong player, should use batting mode"
                value={feedbackNote}
                onChange={(event) => setFeedbackNote(event.target.value)}
                disabled={feedbackSubmitting}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleBadFeedbackSubmit}
                  disabled={feedbackSubmitting || !feedbackNote.trim()}
                  sx={qbButtonSx}
                >
                  {feedbackSubmitting ? 'Submitting...' : 'Submit feedback'}
                </Button>
              </Box>
            </Box>
          )}

          {feedbackSuccess && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: qbColors.accent }}>
              Thanks! Feedback recorded.
            </Typography>
          )}

          {feedbackError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {feedbackError}
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default QueryResults;
